import React from "react";
import { Tooltip } from "@mui/material";
import { DEFAULT_APPEARANCE } from "../mockup-engine";
import DesignAreaEditor from "./DesignAreaEditor";
import {
  AlignBottomIcon,
  AlignCenterXIcon,
  AlignCenterYIcon,
  AlignLeftIcon,
  AlignRightIcon,
  AlignTopIcon,
  FillIcon,
  FitIcon,
} from "./icons";

/** Delayed hover / keyboard-focus description. Works on disabled controls too. */
export function Tip({ title, children, placement = "top", block = false }) {
  if (!title) return children;
  return (
    <Tooltip title={title} placement={placement} enterDelay={450} enterNextDelay={250} arrow describeChild>
      <span className={block ? "sw-tip sw-tip-block" : "sw-tip"}>{children}</span>
    </Tooltip>
  );
}

const ALIGN = [
  ["left", "Align the design to the left edge of the print area", AlignLeftIcon],
  ["hcenter", "Center the design across the print area", AlignCenterXIcon],
  ["right", "Align the design to the right edge of the print area", AlignRightIcon],
  ["top", "Align the design to the top edge of the print area", AlignTopIcon],
  ["vcenter", "Center the design vertically in the print area", AlignCenterYIcon],
  ["bottom", "Align the design to the bottom edge of the print area", AlignBottomIcon],
];

/** Align, Fit / Fill / Reset, the flat print-area editor and Apply to all. */
export function PlacementSection({ design, designWidth, designHeight, onAlign, onFit, onReset, onChange, onCommit, onApplyAll, applyAllDisabled, applyAllHint, headingAction }) {
  const enabled = !!design?.placement;
  return (
    <>
      <div className="sw-panel-heading">
        <h2>Placement</h2>
        {headingAction}
      </div>
      <div className="sw-align-tools" role="group" aria-label="Align design">
        {ALIGN.map(([edge, title, Icon]) => (
          <Tip key={edge} title={title}>
            <button type="button" onClick={() => onAlign(edge)} disabled={!enabled} aria-label={title}>
              <Icon />
            </button>
          </Tip>
        ))}
      </div>
      <div className="sw-fit-tools">
        <Tip title="Show the entire design inside the print area, keeping its proportions">
          <button type="button" className={design?.mode === "fit" ? "is-selected" : ""} onClick={() => onFit("fit")} disabled={!enabled}>
            <FitIcon />
            <span>Fit</span>
          </button>
        </Tip>
        <Tip title="Cover the print area, keeping proportions. Edges may be cropped">
          <button type="button" className={design?.mode === "fill" ? "is-selected" : ""} onClick={() => onFit("fill")} disabled={!enabled}>
            <FillIcon />
            <span>Fill</span>
          </button>
        </Tip>
        <Tip title="Restore the design's default size and position, with no rotation">
          <button type="button" onClick={onReset} disabled={!enabled}>
            Reset
          </button>
        </Tip>
      </div>
      <div className="sw-art-preview" aria-label="Design placement in the print area">
        <DesignAreaEditor
          designWidth={designWidth}
          designHeight={designHeight}
          image={design?.texture}
          placement={design?.placement}
          onChange={onChange}
          onCommit={onCommit}
        />
      </div>
      {designWidth > 0 && <p className="sw-design-area">Print area {designWidth} × {designHeight} px</p>}
      <Tip title={applyAllHint} block>
        <button type="button" className="sw-button sw-apply-all" onClick={onApplyAll} disabled={applyAllDisabled}>
          Apply to all
        </button>
      </Tip>
    </>
  );
}

const pct = { min: 0, max: 200, step: 1, toUi: (v) => Math.round(v * 100), fromUi: (v) => v / 100, unit: "%" };
const APPEARANCE_CONTROLS = [
  { key: "shadows", label: "Shadows", ...pct, help: "Adjust the strength of shadows on your design", unavailable: "This mockup has no shadow layers to adjust" },
  { key: "highlights", label: "Reflections / highlights", ...pct, help: "Adjust reflections and highlights on your design", unavailable: "This mockup has no highlight or reflection layers to adjust" },
  { key: "blur", label: "Blur", min: 0, max: 20, step: 0.5, toUi: (v) => v, fromUi: (v) => v, unit: " px", help: "Soften the design's edges and details to blend it into the photo" },
  { key: "opacity", label: "Transparency", min: 0, max: 100, step: 1, toUi: (v) => Math.round((1 - v) * 100), fromUi: (v) => 1 - v / 100, unit: "%", help: "Make the design more or less transparent" },
];
const TONE_CONTROLS = [
  { key: "brightness", label: "Brightness", min: -100, max: 100, step: 1, toUi: (v) => v, fromUi: (v) => v, unit: "", help: "Make the design lighter or darker" },
  { key: "contrast", label: "Contrast", min: -100, max: 100, step: 1, toUi: (v) => v, fromUi: (v) => v, unit: "", help: "Adjust design contrast" },
];

/**
 * Shadows, highlights, blur, transparency and tone. `availability`
 * ({ shadows, highlights }, from lightingAvailability) greys out lighting
 * sliders that would not change this mockup.
 */
export function AppearanceSection({ appearance, onChange, availability, disabled = false }) {
  const set = (key, value) => onChange({ ...appearance, [key]: value });
  const slider = (c) => {
    const unavailable = c.unavailable && availability && availability[c.key] === false;
    return (
      <Slider
        key={c.key}
        control={c}
        value={unavailable ? DEFAULT_APPEARANCE[c.key] : appearance[c.key]}
        disabled={disabled || unavailable}
        reason={unavailable ? c.unavailable : null}
        onChange={(v) => set(c.key, v)}
      />
    );
  };
  return (
    <>
      <div className="sw-panel-heading">
        <h2>Appearance</h2>
        <Tip title="Reset all the sliders">
          <button type="button" className="sw-text-button" onClick={() => onChange(DEFAULT_APPEARANCE)} disabled={disabled}>
            Reset
          </button>
        </Tip>
      </div>
      {APPEARANCE_CONTROLS.map(slider)}
      <div className="sw-tonal-divider" />
      {TONE_CONTROLS.map(slider)}
    </>
  );
}

function Slider({ control: c, value, onChange, disabled, reason }) {
  const ui = c.toUi(value);
  const id = `sw-${c.key}`;
  const fill = ((ui - c.min) / (c.max - c.min)) * 100;
  return (
    <Tip title={reason || c.help} placement="right" block>
      <div className={`sw-slider${reason ? " is-unavailable" : ""}`}>
        <label htmlFor={id}>
          <span>{c.label}</span>
          <output htmlFor={id}>{`${Math.round(ui * 10) / 10}${c.unit}`}</output>
        </label>
        <input
          id={id}
          type="range"
          min={c.min}
          max={c.max}
          step={c.step}
          value={ui}
          disabled={disabled}
          aria-describedby={reason ? `${id}-reason` : undefined}
          style={{ "--sw-fill": `${fill}%` }}
          onChange={(e) => onChange(c.fromUi(Number(e.target.value)))}
        />
        {reason && (
          <span id={`${id}-reason`} className="sw-visually-hidden">
            {reason}
          </span>
        )}
      </div>
    </Tip>
  );
}
